import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { debounce, batchUpdates } from '../../../Utils/debounce';
import { performanceMonitor } from '../../../Utils/performanceMonitor';

/**
 * Optimized hook for handling message state updates with batching and debouncing
 * Prevents excessive re-renders and improves performance for large message lists
 */
const useOptimizedMessages = (initialMessages = []) => {
  const [messages, setMessages] = useState(initialMessages);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const lastUpdateTime = useRef(Date.now());

  // Batch multiple message updates together
  const batchedMessageUpdate = useMemo(
    () => batchUpdates((updates) => {
      performanceMonitor.trackRender('useOptimizedMessages.batchUpdate', () => {
        setMessages(prevMessages => {
          let updatedMessages = [...prevMessages];

          updates.forEach(update => {
            switch (update.type) {
              case 'ADD':
                // Prevent duplicates
                if (!updatedMessages.some(msg => msg.id === update.data.id)) {
                  updatedMessages = [update.data, ...updatedMessages];
                }
                break;

              case 'UPDATE':
                updatedMessages = updatedMessages.map(msg =>
                  msg.id === update.data.id ? { ...msg, ...update.data } : msg
                );
                break;

              case 'DELETE':
                updatedMessages = updatedMessages.filter(msg => msg.id !== update.data.id);
                break;

              case 'BULK_REPLACE':
                updatedMessages = update.data;
                break;

              default:
                console.warn('Unknown message update type:', update.type);
            }
          });

          lastUpdateTime.current = Date.now();
          return updatedMessages;
        });
      });
    }, 16), // 60fps batching
    []
  );

  // Optimized message addition
  const addMessage = useCallback((message) => {
    batchedMessageUpdate({ type: 'ADD', data: message });
  }, [batchedMessageUpdate]);

  // Optimized message update
  const updateMessage = useCallback((messageUpdate) => {
    batchedMessageUpdate({ type: 'UPDATE', data: messageUpdate });
  }, [batchedMessageUpdate]);

  // Optimized message deletion
  const deleteMessage = useCallback((messageId) => {
    batchedMessageUpdate({ type: 'DELETE', data: { id: messageId } });
  }, [batchedMessageUpdate]);

  // Bulk replace (for initial load or pagination)
  const replaceMessages = useCallback((newMessages) => {
    batchedMessageUpdate({ type: 'BULK_REPLACE', data: newMessages });
  }, [batchedMessageUpdate]);

  // Append messages (for pagination - older messages)
  const appendMessages = useCallback((newMessages) => {
    setMessages(prev => [...prev, ...newMessages]);
  }, []);

  // Prepend messages (for pagination - newer messages)
  const prependMessages = useCallback((newMessages) => {
    setMessages(prev => [...newMessages, ...prev]);
  }, []);

  // Efficient message search with memoization
  const findMessage = useCallback((messageId) => {
    return messages.find(msg => msg.id === messageId);
  }, [messages]);

  // Get recent messages for subscription optimization
  const recentMessages = useMemo(() => {
    return messages.slice(0, 50); // Limit to prevent subscription explosion
  }, [messages]);

  // Performance metrics
  const getPerformanceMetrics = useCallback(() => {
    return {
      messageCount: messages.length,
      lastUpdateTime: lastUpdateTime.current,
      recentMessageCount: recentMessages.length
    };
  }, [messages.length, recentMessages.length]);

  return {
    messages,
    recentMessages,
    addMessage,
    updateMessage,
    deleteMessage,
    replaceMessages,
    appendMessages,
    prependMessages,
    findMessage,
    getPerformanceMetrics,
    messageCount: messages.length
  };
};

export default useOptimizedMessages;
